import { useEffect, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Th } from '../../components/molecules/Table'
import type {
  ProductVariantMaterialsDetailDto,
  ProductVariantMaterialsSummaryDto,
  RawMaterialPickerEntry,
  SyncMaterialsInput
} from '../../../viewmodels/useProductPricing'

const MAX_QTY_MILLIS = 10_000_000_000

interface Props {
  readonly target: ProductVariantMaterialsSummaryDto | null
  readonly onClose: () => void
  readonly fetchDetail: (
    variantId: string
  ) => Promise<{ ok: true; data: ProductVariantMaterialsDetailDto } | { ok: false; message: string }>
  readonly syncMaterials: (
    variantId: string,
    input: SyncMaterialsInput
  ) => Promise<{ ok: true; data: ProductVariantMaterialsDetailDto } | { ok: false; field: string | null; message: string }>
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; data: ProductVariantMaterialsDetailDto }
  | { status: 'error'; message: string }

/**
 * Per-material row state. Every one of the product's raw materials gets
 * exactly one row (keyed by rawMaterialId). A row is saved as a link
 * only when a supplier is selected; an empty supplier means "not used
 * in this variant". qtyText is the form value, converted to millis on
 * submit.
 */
interface MaterialRowState {
  rawMaterialId: string
  supplierId: string
  qtyText: string
}

function millisToText(m: number): string {
  return (m / 1000).toString()
}
function parseQty(text: string, label: string): number {
  const t = text.trim()
  if (t === '') throw new Error(`${label} is required`)
  const n = Number(t)
  if (!Number.isFinite(n) || n <= 0) throw new Error(`${label} must be a positive number`)
  const m = Math.round(n * 1000)
  if (m < 1 || m > MAX_QTY_MILLIS) throw new Error(`${label} is out of range`)
  return m
}
/** Non-throwing parse for the live cost preview; null when not usable. */
function tryQtyMillis(text: string): number | null {
  const t = text.trim()
  if (t === '') return null
  const n = Number(t)
  if (!Number.isFinite(n) || n <= 0) return null
  const m = Math.round(n * 1000)
  if (m < 1 || m > MAX_QTY_MILLIS) return null
  return m
}
function effectivePrice(sp: RawMaterialPickerEntry['supplier_prices'][number]): number {
  return sp.discounted_price_paisa ?? sp.seller_price_paisa
}
/** Matches the cost-rollup worker exactly: round((qty × price) / 1000). */
function rowCostPaisa(qtyMillis: number, pricePaisa: number): number {
  return Math.round((qtyMillis * pricePaisa) / 1000)
}
function formatPaisa(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

export function ProductVariantMaterialsEditModal({
  target,
  onClose,
  fetchDetail,
  syncMaterials
}: Props): JSX.Element {
  const [state, setState] = useState<LoadState | null>(null)
  const [rows, setRows] = useState<MaterialRowState[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [rowError, setRowError] = useState<{ rmId: string; message: string } | null>(null)

  useEffect(() => {
    if (target === null) {
      setState(null)
      setRows([])
      setSubmitting(false)
      setFormError(null)
      setRowError(null)
      return
    }
    let cancelled = false
    setState({ status: 'loading' })
    setFormError(null)
    setRowError(null)
    void fetchDetail(target.variant_id).then((res) => {
      if (cancelled) return
      if (res.ok) {
        setState({ status: 'ready', data: res.data })
        const linkByRm = new Map(res.data.current_links.map((l) => [l.raw_material_id, l] as const))
        setRows(
          res.data.picker.map((p) => {
            const link = linkByRm.get(p.id)
            const supplierStillPriced =
              link !== undefined && p.supplier_prices.some((sp) => sp.supplier_id === link.supplier_id)
            return {
              rawMaterialId: p.id,
              supplierId: supplierStillPriced && link !== undefined ? link.supplier_id : '',
              qtyText: link !== undefined ? millisToText(link.quantity_millis) : ''
            }
          })
        )
      } else {
        setState({ status: 'error', message: res.message })
      }
    })
    return () => {
      cancelled = true
    }
  }, [target, fetchDetail])

  const handleSupChange = (rmId: string, sId: string): void => {
    setRowError(null)
    setRows((rs) => rs.map((r) => (r.rawMaterialId === rmId ? { ...r, supplierId: sId } : r)))
  }
  const handleQtyChange = (rmId: string, qty: string): void => {
    setRowError(null)
    setRows((rs) => rs.map((r) => (r.rawMaterialId === rmId ? { ...r, qtyText: qty } : r)))
  }

  const handleSubmit = async (): Promise<void> => {
    if (target === null || state?.status !== 'ready') return
    setFormError(null)
    setRowError(null)
    const materials: SyncMaterialsInput['materials'][number][] = []
    for (const r of rows) {
      // No supplier → material is not used in this variant.
      if (r.supplierId === '') continue
      let qty: number
      try {
        qty = parseQty(r.qtyText, 'Quantity')
      } catch (e) {
        setRowError({ rmId: r.rawMaterialId, message: e instanceof Error ? e.message : 'Invalid quantity' })
        return
      }
      materials.push({
        raw_material_id: r.rawMaterialId,
        supplier_id: r.supplierId,
        quantity_millis: qty
      })
    }
    setSubmitting(true)
    const result = await syncMaterials(target.variant_id, { materials })
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    setFormError(result.message)
  }

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={
        state?.status === 'ready'
          ? `Materials — ${state.data.product_name}${state.data.axes_label === null || state.data.axes_label === '' ? ' (default)' : ` (${state.data.axes_label})`}`
          : 'Materials'
      }
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={submitting || state?.status !== 'ready'}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      {state === null || state.status === 'loading' ? (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : state.status === 'error' ? (
        <Alert tone="error">{state.message}</Alert>
      ) : (
        <ModalBody
          data={state.data}
          rows={rows}
          formError={formError}
          rowError={rowError}
          onSupChange={handleSupChange}
          onQtyChange={handleQtyChange}
        />
      )}
    </Modal>
  )
}

function ModalBody({
  data,
  rows,
  formError,
  rowError,
  onSupChange,
  onQtyChange
}: {
  readonly data: ProductVariantMaterialsDetailDto
  readonly rows: MaterialRowState[]
  readonly formError: string | null
  readonly rowError: { rmId: string; message: string } | null
  readonly onSupChange: (rmId: string, sId: string) => void
  readonly onQtyChange: (rmId: string, qty: string) => void
}): JSX.Element {
  const pickerByRm = new Map<string, RawMaterialPickerEntry>()
  for (const p of data.picker) pickerByRm.set(p.id, p)
  const linkByRm = new Map(data.current_links.map((l) => [l.raw_material_id, l] as const))

  // Live total of every row that has a supplier + a usable quantity.
  let totalPaisa = 0
  for (const r of rows) {
    if (r.supplierId === '') continue
    const entry = pickerByRm.get(r.rawMaterialId)
    const sp = entry?.supplier_prices.find((x) => x.supplier_id === r.supplierId)
    const qtyMillis = tryQtyMillis(r.qtyText)
    if (sp !== undefined && qtyMillis !== null) totalPaisa += rowCostPaisa(qtyMillis, effectivePrice(sp))
  }

  if (data.picker.length === 0) {
    return (
      <Alert tone="info">
        This product has no raw materials yet. Add them on the{' '}
        <span className="font-medium">Raw Materials</span> screen (and price them on{' '}
        <span className="font-medium">Raw Material Pricing</span>) first, then come back to
        set quantities per supplier.
      </Alert>
    )
  }

  const noActivePricing = data.picker.every((p) => p.supplier_prices.length === 0)

  return (
    <div className="space-y-4">
      {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

      {noActivePricing ? (
        <Alert tone="info">
          None of the raw materials for this product have active supplier pricing yet. Set prices
          on the <span className="font-medium">Raw Material Pricing</span> screen first, then come
          back to set quantities.
        </Alert>
      ) : null}

      <p className="text-body-xs text-text-secondary">
        Set the quantity and supplier for each material this variant uses. A material with no
        supplier selected is not counted. Quantity is in the unit of measure for that material
        (e.g. 1.5 = 1.5 kg).
      </p>

      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full min-w-[640px] font-body text-body-sm">
          <thead className="bg-parchment/50 text-text-secondary">
            <tr>
              <Th>Raw material</Th>
              <Th>Quantity</Th>
              <Th>Supplier</Th>
              <Th className="text-right">Price / unit</Th>
              <Th className="text-right">Cost</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const entry = pickerByRm.get(r.rawMaterialId)
              if (entry === undefined) return null
              const link = linkByRm.get(r.rawMaterialId)
              const linkSupplierMissing =
                link !== undefined && !entry.supplier_prices.some((sp) => sp.supplier_id === link.supplier_id)
              return (
                <MaterialRow
                  key={r.rawMaterialId}
                  row={r}
                  entry={entry}
                  staleSupplierName={linkSupplierMissing && r.supplierId === '' ? link.supplier_name : null}
                  error={rowError !== null && rowError.rmId === r.rawMaterialId ? rowError.message : null}
                  onSupChange={(v) => onSupChange(r.rawMaterialId, v)}
                  onQtyChange={(v) => onQtyChange(r.rawMaterialId, v)}
                />
              )
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-parchment/30">
              <td className="px-3 py-3 font-medium text-text-primary" colSpan={4}>
                Total material cost (preview)
              </td>
              <td className="px-3 py-3 text-right font-mono font-medium whitespace-nowrap">
                {formatPaisa(totalPaisa)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="text-body-xs text-text-secondary">
        Saving stores these materials. To copy this cost onto the variant, click
        <span className="font-medium"> Accept</span> on it in the Product Pricing list.
      </p>
    </div>
  )
}

function MaterialRow({
  row,
  entry,
  staleSupplierName,
  error,
  onSupChange,
  onQtyChange
}: {
  readonly row: MaterialRowState
  readonly entry: RawMaterialPickerEntry
  readonly staleSupplierName: string | null
  readonly error: string | null
  readonly onSupChange: (sId: string) => void
  readonly onQtyChange: (qty: string) => void
}): JSX.Element {
  const hasPricing = entry.supplier_prices.length > 0
  const selected = row.supplierId === '' ? null : entry.supplier_prices.find((sp) => sp.supplier_id === row.supplierId) ?? null
  const qtyMillis = tryQtyMillis(row.qtyText)
  const costPaisa = selected !== null && qtyMillis !== null ? rowCostPaisa(qtyMillis, effectivePrice(selected)) : null

  return (
    <tr className="border-t border-border align-top">
      <td className="px-3 py-3 w-[28%]">
        <span className="text-text-primary">{entry.name}</span>
        <span className="block font-mono text-body-xs text-text-secondary">{entry.unit_label}</span>
      </td>
      <td className="px-3 py-3 w-[22%]">
        <div className="flex items-center gap-2">
          <Input
            className="min-w-[4.5rem]"
            value={row.qtyText}
            onChange={(e) => onQtyChange(e.target.value)}
            placeholder="e.g. 1.5"
            inputMode="decimal"
            disabled={!hasPricing}
            hasError={error !== null}
          />
          <span className="font-mono text-body-xs text-text-secondary whitespace-nowrap">
            {entry.unit_label}
          </span>
        </div>
        {error !== null ? (
          <span className="block text-body-xs font-body text-terra mt-1">{error}</span>
        ) : null}
      </td>
      <td className="px-3 py-3 w-[22%]">
        {hasPricing ? (
          <>
            <Select value={row.supplierId} onChange={(e) => onSupChange(e.target.value)}>
              <option value="">— none —</option>
              {entry.supplier_prices.map((sp) => (
                <option key={sp.supplier_id} value={sp.supplier_id}>
                  {sp.supplier_name} — {formatPaisa(effectivePrice(sp))}
                </option>
              ))}
            </Select>
            {staleSupplierName !== null ? (
              <span className="block text-body-xs font-body text-terra mt-1">
                {`"${staleSupplierName}" no longer prices this material — pick another.`}
              </span>
            ) : null}
          </>
        ) : (
          <span className="text-body-xs text-text-secondary">
            No supplier pricing — set it on the Raw Material Pricing screen.
          </span>
        )}
      </td>
      <td className="px-3 py-3 w-[14%] text-right font-mono whitespace-nowrap">
        {selected === null ? (
          <span className="text-text-secondary">—</span>
        ) : selected.discounted_price_paisa !== null ? (
          <>
            {formatPaisa(selected.discounted_price_paisa)}
            <span className="ml-1 text-text-secondary line-through">
              {formatPaisa(selected.seller_price_paisa)}
            </span>
          </>
        ) : (
          formatPaisa(selected.seller_price_paisa)
        )}
      </td>
      <td className="px-3 py-3 w-[14%] text-right font-mono whitespace-nowrap">
        {costPaisa === null ? <span className="text-text-secondary">—</span> : formatPaisa(costPaisa)}
      </td>
    </tr>
  )
}
